'use client';

import {
  CAD_CURRENCY,
  CURRENCY_COOKIE,
  USD_CURRENCY,
  USD_RATE,
} from '@utils/currency';
import devConsole from '@utils/developer-console';
import { getCookie } from 'cookies-next';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

interface CurrencyContextProps {
  priceRate: number;
  priceRateLoading: boolean;
  currency: string;
  setCurrency: (value: string) => void;
}

const defaultCurrencyContext: CurrencyContextProps = {
  priceRate: 1,
  priceRateLoading: false,
  currency: USD_CURRENCY,
  setCurrency: () => {},
};

const CurrencyContext = createContext<CurrencyContextProps>(
  defaultCurrencyContext
);

export const useCurrencyContext = () => useContext(CurrencyContext);

export const CurrencyContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [priceRate, setPriceRate] = useState(USD_RATE);
  const [priceRateLoading, setPriceRateLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const [currency, setCurrencyState] = useState(USD_CURRENCY);
  const setCurrency = useCallback(
    (value: string) => {
      const nextCurrency = value === CAD_CURRENCY ? CAD_CURRENCY : USD_CURRENCY;
      if (nextCurrency === currency) return;
      setCurrencyState(nextCurrency);
      if (nextCurrency === CAD_CURRENCY) {
        setPriceRateLoading(true);
      } else {
        setPriceRate(USD_RATE);
        setPriceRateLoading(false);
      }
    },
    [currency]
  );
  const TWO_MINUTES_IN_SECONDS = 120;

  useEffect(() => {
    const savedCurrency = getCookie(CURRENCY_COOKIE) as string;
    setCurrencyState(
      savedCurrency === CAD_CURRENCY ? CAD_CURRENCY : USD_CURRENCY
    );
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (!initialized) return;
    let isActive = true;

    async function fetchData() {
      setPriceRateLoading(true);
      try {
        const res = await fetch(
          `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json`,
          {
            next: { revalidate: TWO_MINUTES_IN_SECONDS },
          }
        );
        const data = await res.json();
        const rate = Number(data.usd?.cad);
        if (!res.ok || !Number.isFinite(rate) || rate <= 0)
          throw new Error('Invalid CAD exchange rate');
        if (isActive) setPriceRate(Number(rate.toFixed(2)));
      } catch (e) {
        devConsole(e);
        if (isActive) {
          setCurrencyState(USD_CURRENCY);
          setPriceRate(USD_RATE);
        }
      } finally {
        if (isActive) setPriceRateLoading(false);
      }
    }

    if (currency === CAD_CURRENCY) {
      fetchData();
    } else {
      setPriceRate(USD_RATE);
      setPriceRateLoading(false);
    }

    return () => {
      isActive = false;
    };
  }, [currency, initialized]);

  return (
    <CurrencyContext.Provider
      value={{
        priceRate,
        currency,
        setCurrency,
        priceRateLoading,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};
